import * as ProgressPrimitive from '@radix-ui/react-progress';
import { Root } from '@radix-ui/react-progress';
import { ComponentProps } from 'react';

import { mergeClasses } from '@/utils';

interface IProgress extends ComponentProps<typeof Root> {
  value: number;
  label?: string;
  size?: 'default' | 'sm';
  disabled?: boolean;
  variant?: 'green' | 'yellow' | 'blue';
}

export const ProgressBar = ({ className, value, label, size = 'default', ...props }: IProgress) => {
  return (
    <div className={mergeClasses('w-full flex flex-col gap-2', className)}>
      <div className="grid grid-cols-[1fr_auto] items-center gap-2">
        <span
          className="text-body-sm font-medium text-neutral-9 dark:text-neutral-0 truncate"
          title={label}
        >
          {label}
        </span>
        <span className="text-body-sm font-medium text-neutral-9 dark:text-neutral-0">
          {value}%
        </span>
      </div>
      <ProgressPrimitive.Root
        data-slot="progress"
        className={mergeClasses(
          'bg-neutral-3 dark:bg-neutral-9 relative w-full overflow-hidden rounded-full',
          size === 'sm' ? 'h-2' : 'h-4',
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          data-slot="progress-indicator"
          className="bg-primary-base h-full w-full flex-1 transition-all rounded-full"
          style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
        />
      </ProgressPrimitive.Root>
    </div>
  );
};

export const SimpleProgressBar = ({
  className,
  value,
  size = 'default',
  disabled,
  variant = 'blue',
  ...props
}: IProgress) => {
  return (
    <div className={mergeClasses('w-full flex flex-col gap-2', className)}>
      <ProgressPrimitive.Root
        data-slot="progress"
        className={mergeClasses(
          'bg-neutral-3 dark:bg-neutral-9 relative w-full overflow-hidden rounded-full',
          size === 'sm' ? 'h-2' : 'h-4',
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          data-slot="progress-indicator"
          className={mergeClasses(
            'border border-opacity-light-10 h-full w-full flex-1 transition-all rounded-full',
            variant === 'blue' && 'bg-primary-base',
            variant === 'green' && 'bg-green-base',
            variant === 'yellow' && 'bg-yellow-base',
            disabled && '!bg-neutral-5',
          )}
          style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
        />
      </ProgressPrimitive.Root>
    </div>
  );
};
